import React, { useState, useEffect, useMemo } from "react";
import { message, Alert, Checkbox, Statistic, Row, Col } from "antd";
import CriticalActionModal from "./CriticalActionModal";
import { rollbackMacaronPayment } from "../api/subscriptions/subscriptionsRequest";

const fmt = (v) =>
  v === null || v === undefined || isNaN(Number(v)) ? "—" : `${Number(v).toFixed(2)} DH`;

/**
 * New case — Cancel (rollback) a macaron payment.
 * Soft-deletes the payment + details + transaction and optionally deducts the amount
 * from the collecting agent's balance. Shows the projected balance + negative warning.
 */
const RollbackMacaronPaymentModal = ({ visible, onCancel, onSuccess, payment }) => {
  const [loading, setLoading] = useState(false);
  const [updateAgentSolde, setUpdateAgentSolde] = useState(true);

  useEffect(() => {
    if (visible) setUpdateAgentSolde(true);
  }, [visible]);

  const agent = payment?.user || null;
  const amount = Number(payment?.amount ?? 0);
  const currentBalance =
    agent && agent.balance !== undefined && agent.balance !== null ? Number(agent.balance) : null;

  const projectedBalance = useMemo(() => {
    if (currentBalance === null || !updateAgentSolde) return currentBalance;
    return Number((currentBalance - amount).toFixed(2));
  }, [currentBalance, amount, updateAgentSolde]);

  const willGoNegative = updateAgentSolde && projectedBalance !== null && projectedBalance < 0;

  const handleConfirm = async (reason) => {
    if (!payment) return;
    setLoading(true);
    try {
      const response = await rollbackMacaronPayment(payment.id, {
        reason,
        update_agent_solde: updateAgentSolde,
      });
      if (response?.success) {
        message.success(response.message || "Paiement macaron annulé avec succès");
        onSuccess?.(response.data);
      } else {
        message.error(response?.message || response?.error || "Erreur lors de l'annulation");
      }
    } catch (error) {
      message.error(error.response?.data?.error || error.response?.data?.message || "Erreur lors de l'annulation du paiement macaron");
    } finally {
      setLoading(false);
    }
  };

  return (
    <CriticalActionModal
      visible={visible}
      onCancel={onCancel}
      onConfirm={handleConfirm}
      loading={loading}
      danger
      title="Annuler le paiement macaron"
      okText="Annuler le paiement"
      confirmPhrase={payment?.reference || (payment?.id ? `#${payment.id}` : null)}
      summaryItems={[
        { label: "Paiement", to: payment?.reference || `#${payment?.id ?? ""}` },
        { label: "Type", to: "Macaron" },
        { label: "Montant", to: fmt(amount) },
      ]}
      warningBanner={
        <>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 8 }}
            message="Action irréversible côté métier"
            description="Le paiement macaron, son détail et la transaction associée seront supprimés."
          />
          {willGoNegative && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 8 }}
              message="Attention : solde agent négatif"
              description={`Cette action laissera l'agent avec un solde négatif de ${fmt(projectedBalance)}.`}
            />
          )}
        </>
      }
    >
      <div style={{ marginTop: 12 }}>
        <Checkbox checked={updateAgentSolde} onChange={(e) => setUpdateAgentSolde(e.target.checked)}>
          Ajuster le solde de l'agent (déduire {fmt(amount)})
        </Checkbox>

        {currentBalance !== null ? (
          <Row gutter={16} style={{ marginTop: 12 }}>
            <Col span={12}>
              <Statistic title="Solde actuel de l'agent" value={currentBalance} precision={2} suffix="DH" />
            </Col>
            <Col span={12}>
              <Statistic
                title="Solde après l'action"
                value={projectedBalance ?? currentBalance}
                precision={2}
                suffix="DH"
                valueStyle={{ color: willGoNegative ? "#cf1322" : undefined }}
              />
            </Col>
          </Row>
        ) : (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            message="Solde de l'agent indisponible ; l'ajustement sera appliqué côté serveur si l'option est cochée."
          />
        )}
      </div>
    </CriticalActionModal>
  );
};

export default RollbackMacaronPaymentModal;
